import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CONTRACTOR_LEAD_DAYS,
  deliveryHoursProblem,
  deliveryTimesOn,
  earliestNeededBy,
  formatMoney,
  formatTime,
  isActiveContractor,
  effectivePrice,
  storeHoursOn,
  WEEKDAY_NAMES,
  weekday,
  type ContractorOrder,
  type ContractorOrderStatus,
  type ContractorQuote,
  type ContractorStatus,
  type SearchSuggestion,
} from "@neus/shared";
import { api } from "../api/client";
import { useContractorOrder, useContractorOrders, useContractorQuote, useMe } from "../api/hooks";
import {
  BadgeIcon,
  BoxesIcon,
  CalendarIcon,
  CartIcon,
  ClipboardIcon,
  HardHatIcon,
  TruckIcon,
  WalletIcon,
} from "../components/Icons";
import { DepositPayment } from "../components/payment/DepositPayment";
import { Price } from "../components/product/bits";
import { SearchBox } from "../components/search/SearchBox";
import { IconCard, LoadError, PageTitle } from "../components/ui/IconCard";
import styles from "./Contractors.module.css";
import { CONTENT } from "../content";
import { features } from "../features";
import { usePageTitle } from "../usePageTitle";

const message = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong. Please try again.");

const STATUS_LABELS: Record<ContractorOrderStatus, string> = {
  awaiting_deposit: "Awaiting deposit",
  deposit_paid: "Deposit paid",
  balance_invoiced: "Balance invoiced",
  placed: "Placed",
};

/** "+14145550123" → "(414) 555-0123"; other numbers as stored. */
export const formatPhone = (e164: string) => {
  const us = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return us ? `(${us[1]}) ${us[2]}-${us[3]}` : e164;
};

const formatDate = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

/** Page titles, shared by each page and its signed-out, loading and error states. */
const TITLES = {
  orders: "Your job orders",
  newOrder: "New job order",
  order: "Job order",
};

const BENEFITS = [
  {
    icon: <BoxesIcon />,
    title: "Not limited by what’s on the shelf",
    text: "Job orders aren’t capped by today’s stock. We order in what the job needs.",
  },
  {
    icon: <CalendarIcon />,
    title: "Ready when you need it",
    text: `Choose a needed-by date at least ${CONTRACTOR_LEAD_DAYS} days out, and we’ll have it staged for pickup${features.jobSiteDelivery ? " or delivered to the job site" : ""}.`,
  },
  features.contractorDeposits
    ? {
        icon: <WalletIcon />,
        title: "Deposit now, balance later",
        text: "Pay a deposit to place the order. We bill the balance when it’s ready.",
      }
    : {
        icon: <WalletIcon />,
        title: "Nothing to pay up front",
        text: `Place the order now, and pay for it when you pick it up${features.jobSiteDelivery ? " or we deliver it" : ""}.`,
      },
  {
    icon: <BadgeIcon />,
    title: "Company-only deals",
    text: "Add your company’s details for deals and opportunities just for contractors.",
  },
];

const STEPS = [
  {
    title: "Create an account",
    text: features.contractorApproval
      ? "Check “I’m a contractor” on your account. The store reviews it, then you can order."
      : "Check “I’m a contractor” on your account. Approval is automatic.",
  },
  { title: "Start an order", text: "Give it a title, a description and a needed-by date." },
  { title: "Add products", text: "Search for products and set quantities. Prices and totals update as you go." },
  features.contractorDeposits
    ? { title: "Pay the deposit", text: "We’ll be in touch when your order is ready, and bill the balance then." }
    : { title: "Place the order", text: "We’ll be in touch when your order is ready. Pay for it at pickup." },
];

/** The public page: what the program offers, and the way in for each kind of visitor. */
export function ContractorsPage() {
  usePageTitle(CONTENT.site.pageMeta["/contractors"]?.title);
  const { data: me } = useMe();
  const cta = !me ? (
    <Link className="btn btn--lg" to="/account/sign-in?next=%2Faccount%3Fcontractor%3D1">
      Create a free account
    </Link>
  ) : !me.contractor.isContractor ? (
    <Link className="btn btn--lg" to="/account?contractor=1">
      Turn on contractor ordering
    </Link>
  ) : !isActiveContractor(me) ? (
    <ContractorStatusNotice status={me.contractor.status} />
  ) : (
    <Link className="btn btn--lg" to="/contractor/orders/new">
      Start a job order
    </Link>
  );
  // The closing words match the visitor, like the button does.
  const closing = !me
    ? {
        title: "Ready to get started?",
        text: features.contractorApproval
          ? "It takes a minute, and the store reviews each account before it can order."
          : "It takes a minute, and approval is automatic.",
      }
    : !me.contractor.isContractor
      ? {
          title: "Ready to get started?",
          text: "Check “I’m a contractor” on your account and add your company’s name.",
        }
      : { title: "Ready for the next job?", text: "Start a job order, or see the orders you’ve placed." };
  return (
    <div className="container section">
      <div className={styles.page}>
        <PageTitle
          subtitle={
            features.contractorDeposits
              ? "Order for the whole job up front, with a deposit now and the balance later."
              : "Order for the whole job up front, and pay when you pick it up."
          }
        >
          Contractor program
        </PageTitle>
        <IconCard icon={<HardHatIcon />} title={"Plan the job, and we’ll have it ready"}>
          <p className={styles.lead}>
            Order everything for a project up front, pick it up when you need it, and{" "}
            {features.contractorDeposits ? "pay a deposit now with the balance later." : "pay for it then."}
          </p>
          <div>{cta}</div>
        </IconCard>

        <h2 className={styles.sectionTitle}>What you get</h2>
        <div className={styles.grid}>
          {BENEFITS.map((b) => (
            <IconCard key={b.title} icon={b.icon} title={b.title} as="h3">
              <p>{b.text}</p>
            </IconCard>
          ))}
        </div>

        <h2 className={styles.sectionTitle}>How it works</h2>
        {/* role="list": Safari drops list semantics from a list-style: none list. The badge's number is
            decorative, so the heading says the step for screen readers. */}
        <ol className={styles.steps} role="list">
          {STEPS.map((s, i) => (
            <li key={s.title}>
              <IconCard
                step={i + 1}
                title={
                  <>
                    <span className="visually-hidden">Step {i + 1}: </span>
                    {s.title}
                  </>
                }
                as="h3"
              >
                <p>{s.text}</p>
              </IconCard>
            </li>
          ))}
        </ol>

        <IconCard icon={<HardHatIcon />} title={closing.title}>
          <p>{closing.text}</p>
          <div>{cta}</div>
        </IconCard>
      </div>
    </div>
  );
}

/** Contractor-only pages: sign in first, then the contractor box on the profile, then (with approval on) activated. */
function RequireContractor({ title, children }: { title: string; children: ReactNode }) {
  const { data: me, isLoading, isError, refetch } = useMe();
  const { pathname, search } = useLocation();
  const frame = (content: ReactNode) => (
    <>
      <PageTitle>{title}</PageTitle>
      {content}
    </>
  );
  if (isLoading) return frame(<p className="muted">Loading…</p>);
  if (isError) {
    return frame(<LoadError onRetry={() => void refetch()}>We couldn&rsquo;t load your account just now.</LoadError>);
  }
  if (!me) return <Navigate to={`/account/sign-in?next=${encodeURIComponent(pathname + search)}`} replace />;
  if (!me.contractor.isContractor) {
    return frame(
      <IconCard icon={<HardHatIcon />} title="For contractor accounts">
        <p>
          Contractor ordering is for contractor accounts. <Link to="/account?contractor=1">Turn it on</Link> in your
          account ({features.contractorApproval ? "the store reviews it first" : "approval is automatic"}), or{" "}
          <Link to="/contractors">learn more</Link>.
        </p>
      </IconCard>,
    );
  }
  if (!isActiveContractor(me)) {
    return frame(
      <IconCard icon={<HardHatIcon />} title="For contractor accounts">
        <ContractorStatusNotice status={me.contractor.status} />
      </IconCard>,
    );
  }
  return <>{children}</>;
}

/** With contractor approval on: why a contractor account can't order yet (awaiting approval, or deactivated). */
export function ContractorStatusNotice({ status }: { status: ContractorStatus | undefined }) {
  return status === "inactive" ? (
    <div className="notice notice--warn">
      Your contractor account is turned off, so you can&rsquo;t place job orders or get contractor prices. Contact the
      store to turn it back on.
    </div>
  ) : (
    <div className="notice">
      Your contractor account is waiting for the store to approve it. Once it&rsquo;s approved, you can place job orders
      and get contractor prices.
    </div>
  );
}

/** Totals under the lines: subtotal, tax, fee, discounts, total, then the deposit split. */
function QuoteSummary({
  quote,
  pending,
  delivery,
}: {
  quote: ContractorQuote | null;
  pending?: boolean;
  /** Job-site delivery: the fee line is the delivery fee. */
  delivery?: boolean;
}) {
  const row = (label: ReactNode, value: ReactNode, cls?: string) => (
    <div className={cls ? `${styles.totalRow} ${cls}` : styles.totalRow}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
  if (!quote) return <div className={styles.totals}>{row("Order total", "—", styles.grand)}</div>;
  return (
    <div className={styles.totals} aria-busy={pending}>
      {row("Subtotal", formatMoney(quote.subtotal))}
      {row(
        "Tax",
        quote.tax === null ? <span className="muted">Calculated at confirmation</span> : formatMoney(quote.tax),
      )}
      {row(delivery ? "Delivery fee" : "Stocking / shipping fee", formatMoney(quote.fee))}
      {row("Discounts", quote.discount ? `−${formatMoney(quote.discount)}` : formatMoney(0))}
      {row("Order total", formatMoney(quote.total), styles.grand)}
      {quote.depositPercent > 0 &&
        row(`Deposit due now (${quote.depositPercent}%)`, <strong>{formatMoney(quote.deposit)}</strong>, styles.split)}
      {row(
        quote.depositPercent > 0 ? "Balance due later" : "Due at pickup or delivery",
        <>
          {formatMoney(quote.balance)}
          {quote.tax === null && <span className="muted"> + tax</span>}
        </>,
        quote.depositPercent > 0 ? undefined : styles.split,
      )}
    </div>
  );
}

function LinesTable({
  lines,
  onQty,
  onRemove,
}: {
  lines: { sku: string; name: string; qty: number; unitPrice: number; lineTotal: number; regularUnitPrice?: number }[];
  onQty?: (sku: string, qty: number) => void;
  onRemove?: (sku: string) => void;
}) {
  return (
    <table className={styles.lines}>
      <thead>
        <tr>
          <th scope="col">Product</th>
          <th scope="col" className={styles.num}>
            Qty
          </th>
          <th scope="col" className={styles.num}>
            Unit price
          </th>
          <th scope="col" className={styles.num}>
            Line total
          </th>
          {onRemove && (
            <th scope="col">
              <span className="visually-hidden">Remove</span>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.sku}>
            <td>
              <Link to={`/products/${encodeURIComponent(l.sku)}`}>{l.name}</Link>
              <div className="muted">{l.sku}</div>
            </td>
            <td className={styles.num}>
              {onQty ? (
                <input
                  className={`input ${styles.qty}`}
                  type="number"
                  min={1}
                  max={9999}
                  value={l.qty}
                  aria-label={`Quantity of ${l.name}`}
                  onChange={(e) => onQty(l.sku, Math.min(9999, Math.max(1, Math.floor(Number(e.target.value)) || 1)))}
                />
              ) : (
                l.qty
              )}
            </td>
            <td className={styles.num}>
              <Price price={l.unitPrice} regularPrice={l.regularUnitPrice} />
            </td>
            <td className={styles.num}>{formatMoney(l.lineTotal)}</td>
            {onRemove && (
              <td>
                <button type="button" className="btn btn--ghost" onClick={() => onRemove(l.sku)}>
                  Remove
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface DeliveryDraft {
  address: { line1: string; line2: string; city: string; state: string; postalCode: string };
  contactName: string;
  contactPhone: string;
  window: { from: string; to: string };
}

const EMPTY_DELIVERY: DeliveryDraft = {
  address: { line1: "", line2: "", city: "", state: "WI", postalCode: "" },
  contactName: "",
  contactPhone: "",
  window: { from: "08:00", to: "12:00" },
};

/** "Thu, Nov 12, 2026, 8:00 am–12:00 pm": a delivery's date and window. */
const deliveryWhen = (neededBy: string, window: { from: string; to: string }) =>
  `${formatDate(neededBy)}, ${formatTime(window.from)}–${formatTime(window.to)}`;

/** "Delivery on Thu, Nov 12, 2026, 8:00 am–12:00 pm" or "Needed by Thu, Nov 12, 2026". */
const whenText = (o: Pick<ContractorOrder, "neededBy" | "delivery">) =>
  o.delivery ? `Delivery on ${deliveryWhen(o.neededBy, o.delivery.window)}` : `Needed by ${formatDate(o.neededBy)}`;

/** The next day (YYYY-MM-DD). */
const nextDay = (date: string) => new Date(Date.parse(`${date}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

/** The first day on or after a date that the store is open (the date itself when it isn't a valid day). */
const nextOpenDay = (date: string) => {
  if (Number.isNaN(weekday(date))) return date;
  let day = date;
  for (let i = 0; i < 7 && !storeHoursOn(day); i++) day = nextDay(day);
  return day;
};

/** A delivery window moved inside a day's delivery times, as close to the chosen one as it fits. */
function fitWindow(window: { from: string; to: string }, times: string[]) {
  if (times.length < 2) return window;
  const clamp = (t: string, options: string[]) =>
    options.includes(t) ? t : t < options[0]! ? options[0]! : options.at(-1)!;
  const from = clamp(window.from, times.slice(0, -1));
  return {
    from,
    to: clamp(
      window.to,
      times.filter((t) => t > from),
    ),
  };
}

interface DraftLine {
  sku: string;
  name: string;
  qty: number;
  unitPrice: number;
}

/**
 * The delivery rules the server applies, loaded once delivery is chosen: they bring zod, which is
 * too large for the bundle every page loads.
 */
function useDeliverySchemas(wanted: boolean) {
  const [schemas, setSchemas] = useState<typeof import("@neus/shared/delivery") | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!wanted || schemas) return;
    import("@neus/shared/delivery").then(
      (loaded) => {
        setSchemas(loaded);
        setFailed(false);
      },
      () => setFailed(true),
    );
  }, [wanted, schemas]);
  return { schemas, failed };
}

function NewOrderForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [earliest] = useState(() => earliestNeededBy(Date.now()));
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [neededBy, setNeededBy] = useState(earliest);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [deliver, setDeliver] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [delivery, setDelivery] = useState<DeliveryDraft>(EMPTY_DELIVERY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { schemas, failed: schemasFailed } = useDeliverySchemas(deliver);
  // The same rules the server applies; the quote uses the delivery once it's complete.
  const checkedDelivery = deliver ? schemas?.JobSiteDeliverySchema.safeParse(delivery) : undefined;
  const validDelivery = checkedDelivery?.success ? checkedDelivery.data : undefined;
  // Only the address changes the price (fee and tax), so the quote follows it alone: typing the
  // supervisor's name or changing the window doesn't re-price.
  const checkedAddress = deliver ? schemas?.DeliveryAddressSchema.safeParse(delivery.address) : undefined;
  const quoteAddress = checkedAddress?.success ? checkedAddress.data : undefined;
  // Deliveries happen while the store is open: that day's times, and none on a closed day.
  const hours = storeHoursOn(neededBy);
  const times = deliveryTimesOn(neededBy);
  const closedDelivery = deliver && !hours && !Number.isNaN(weekday(neededBy));
  const changeDate = (date: string) => {
    setNeededBy(date);
    setDelivery((d) => ({ ...d, window: fitWindow(d.window, deliveryTimesOn(date)) }));
  };
  const setAddress = (field: keyof DeliveryDraft["address"], value: string) =>
    setDelivery((d) => ({ ...d, address: { ...d.address, [field]: value } }));
  // Re-priced on the server as lines change: it prices from the catalog and adds tax and fees.
  const { query: priced, current } = useContractorQuote(
    lines.map(({ sku, qty }) => ({ sku, qty })),
    quoteAddress,
  );
  const quote = lines.length ? (priced.data ?? null) : null;
  // Until the quote matches the lines on screen, the button can't place the order at an old price.
  const quoting = lines.length > 0 && (!current || priced.isFetching);

  const add = (s: SearchSuggestion) =>
    setLines((ls) =>
      ls.some((l) => l.sku === s.sku)
        ? ls.map((l) => (l.sku === s.sku ? { ...l, qty: Math.min(9999, l.qty + 1) } : l))
        : [...ls, { sku: s.sku, name: s.name, qty: 1, unitPrice: s.price }],
    );
  const { data: me } = useMe();
  const shown = lines.map((l) => {
    // The last quoted unit price holds while a quantity changes (the server knows which special
    // prices a job order discount beats); a product not quoted yet shows the contractor price.
    const priced = quote?.lines.find((q) => q.sku === l.sku);
    const local = effectivePrice({ sku: l.sku, price: l.unitPrice }, me);
    const unitPrice = priced?.unitPrice ?? local.price;
    return {
      ...l,
      unitPrice,
      lineTotal: priced?.qty === l.qty ? priced.lineTotal : unitPrice * l.qty,
      regularUnitPrice: priced ? priced.regularUnitPrice : local.regularPrice,
    };
  });

  const submit = async () => {
    setError(null);
    if (!title.trim()) return setError("Give the order a title.");
    if (!lines.length) return setError("Add at least one product.");
    if (neededBy < earliest) return setError(`Choose a date on or after ${formatDate(earliest)}.`);
    if (deliver) {
      if (!checkedDelivery?.success) {
        return setError(checkedDelivery?.error.issues[0]?.message ?? "Check the delivery details.");
      }
      const problem = deliveryHoursProblem(neededBy, delivery.window);
      if (problem) return setError(problem);
    }
    setSaving(true);
    try {
      const created = await api.createContractorOrder({
        title,
        description,
        neededBy,
        lines: lines.map(({ sku, qty }) => ({ sku, qty })),
        ...(validDelivery && { delivery: validDelivery }),
        ...(features.anonymousJobOrders && anonymous && { anonymous: true }),
      });
      void qc.invalidateQueries({ queryKey: ["contractor-orders"] });
      navigate(`/contractor/orders/${created.order.id}`, { state: { clientSecret: created.clientSecret } });
    } catch (err) {
      setError(message(err));
      setSaving(false);
    }
  };

  return (
    <div className={styles.stack}>
      <PageTitle subtitle={`Order for the job: picked up or delivered at least ${CONTRACTOR_LEAD_DAYS} days out.`}>
        {TITLES.newOrder}
      </PageTitle>
      <IconCard icon={<ClipboardIcon />} title="Order details">
        <label className={styles.field}>
          <span>Title</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="e.g. Maple St. kitchen remodel"
          />
          {(features.anonymousPickSheets || (features.anonymousJobOrders && anonymous)) && (
            <span className={styles.fieldNote}>
              Store staff see this title on the sheet they print for your order. For privacy, leave your company&rsquo;s
              and your client&rsquo;s names and the job site address out of it.
            </span>
          )}
        </label>
        {features.anonymousJobOrders && (
          <label className={styles.check}>
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
            <span>
              <strong>Keep this order anonymous in the store</strong>
              <span className="muted">
                {" "}
                (the sheet staff print for it shows only its title, not who it&rsquo;s for)
              </span>
            </span>
          </label>
        )}
        <label className={styles.field}>
          <span>Description</span>
          <textarea
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            placeholder="Anything we should know: job site, staging, substitutions…"
          />
        </label>
        {features.jobSiteDelivery && (
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={deliver}
              onChange={(e) => {
                setDeliver(e.target.checked);
                // No deliveries while the store is closed: move to the next open day rather than wait for submit.
                if (e.target.checked) changeDate(nextOpenDay(neededBy));
              }}
            />
            <span>
              <strong>Deliver to the job site</strong>
              <span className="muted"> (otherwise, pick it up at the store)</span>
            </span>
          </label>
        )}
        <div className={styles.when}>
          <label className={styles.field}>
            <span>
              {deliver ? "Needed on" : "Needed by"} (at least {CONTRACTOR_LEAD_DAYS} days out
              {deliver && ", Monday to Saturday"})
            </span>
            <input
              className={`input ${styles.date}`}
              type="date"
              min={earliest}
              value={neededBy}
              onChange={(e) => changeDate(e.target.value)}
              aria-invalid={closedDelivery || undefined}
              aria-describedby={closedDelivery ? "closed-note" : undefined}
            />
            {closedDelivery && (
              <span id="closed-note" className={styles.fieldError}>
                {deliveryHoursProblem(neededBy, delivery.window)}
              </span>
            )}
          </label>
          {deliver && hours && (
            <fieldset className={styles.window}>
              <legend>
                Delivery window{" "}
                <span className="muted">
                  ({WEEKDAY_NAMES[weekday(neededBy)]}s, {formatTime(hours.open)}–{formatTime(hours.close)})
                </span>
              </legend>
              <label>
                <span className="visually-hidden">From</span>
                <select
                  className="select"
                  value={delivery.window.from}
                  onChange={(e) => {
                    const from = e.target.value;
                    // Keep the window valid: "to" moves to the next slot when it's no longer after "from".
                    setDelivery((d) => ({
                      ...d,
                      window: {
                        from,
                        to: d.window.to > from ? d.window.to : times[times.indexOf(from) + 1]!,
                      },
                    }));
                  }}
                >
                  {times.slice(0, -1).map((t) => (
                    <option key={t} value={t}>
                      {formatTime(t)}
                    </option>
                  ))}
                </select>
              </label>
              <span aria-hidden="true">to</span>
              <label>
                <span className="visually-hidden">To</span>
                <select
                  className="select"
                  value={delivery.window.to}
                  onChange={(e) => setDelivery((d) => ({ ...d, window: { ...d.window, to: e.target.value } }))}
                >
                  {times
                    .filter((t) => t > delivery.window.from)
                    .map((t) => (
                      <option key={t} value={t}>
                        {formatTime(t)}
                      </option>
                    ))}
                </select>
              </label>
            </fieldset>
          )}
        </div>

        {deliver && (
          <div className={styles.delivery}>
            <h3 className={styles.subhead}>
              <TruckIcon size={18} /> Job site
            </h3>
            <label className={styles.field}>
              <span>Street address</span>
              <input
                className="input"
                value={delivery.address.line1}
                onChange={(e) => setAddress("line1", e.target.value)}
                autoComplete="address-line1"
                maxLength={120}
              />
            </label>
            <label className={styles.field}>
              <span>Address line 2 (optional)</span>
              <input
                className="input"
                value={delivery.address.line2}
                onChange={(e) => setAddress("line2", e.target.value)}
                autoComplete="address-line2"
                maxLength={120}
                placeholder="Lot, building, gate code…"
              />
            </label>
            <div className={styles.cityRow}>
              <label className={styles.field}>
                <span>City</span>
                <input
                  className="input"
                  value={delivery.address.city}
                  onChange={(e) => setAddress("city", e.target.value)}
                  autoComplete="address-level2"
                  maxLength={80}
                />
              </label>
              <label className={styles.field}>
                <span>State</span>
                <input
                  className="input"
                  value={delivery.address.state}
                  onChange={(e) => setAddress("state", e.target.value.toUpperCase())}
                  autoComplete="address-level1"
                  maxLength={2}
                />
              </label>
              <label className={styles.field}>
                <span>ZIP</span>
                <input
                  className="input"
                  value={delivery.address.postalCode}
                  onChange={(e) => setAddress("postalCode", e.target.value)}
                  autoComplete="postal-code"
                  inputMode="numeric"
                  maxLength={10}
                />
              </label>
            </div>
            <h3 className={styles.subhead}>Job site supervisor</h3>
            <p className="muted">We&rsquo;ll call to confirm the delivery.</p>
            <div className={styles.contactRow}>
              <label className={styles.field}>
                <span>Name</span>
                <input
                  className="input"
                  value={delivery.contactName}
                  onChange={(e) => setDelivery((d) => ({ ...d, contactName: e.target.value }))}
                  maxLength={100}
                />
              </label>
              <label className={styles.field}>
                <span>Phone</span>
                <input
                  className="input"
                  type="tel"
                  value={delivery.contactPhone}
                  onChange={(e) => setDelivery((d) => ({ ...d, contactPhone: e.target.value }))}
                  placeholder="414-555-0123"
                />
              </label>
            </div>
          </div>
        )}
      </IconCard>

      <IconCard icon={<CartIcon />} title="Products">
        <SearchBox
          onPick={add}
          clearOnPick
          label="Add a product"
          placeholder="Search to add products by name, brand or SKU…"
        />
        {lines.length ? (
          <LinesTable
            lines={shown}
            onQty={(sku, qty) => setLines((ls) => ls.map((l) => (l.sku === sku ? { ...l, qty } : l)))}
            onRemove={(sku) => setLines((ls) => ls.filter((l) => l.sku !== sku))}
          />
        ) : (
          <p className="muted">No products yet. Search above to add them.</p>
        )}
        {deliver &&
          !schemas &&
          (schemasFailed ? (
            <p className="notice notice--warn" role="alert">
              We couldn&rsquo;t load the delivery details, so the total doesn&rsquo;t include delivery. Reload the page
              to try again (you&rsquo;ll need to enter this order again), or choose pickup.
            </p>
          ) : (
            <p className="muted">Loading the delivery details…</p>
          ))}
        <QuoteSummary quote={quote} pending={quoting} delivery={Boolean(quoteAddress)} />
      </IconCard>

      {(error ?? (priced.error && lines.length)) && (
        <div className="notice notice--warn" role="alert">
          {error ?? message(priced.error)}
        </div>
      )}
      <div className={styles.row}>
        <button
          type="button"
          className="btn btn--lg"
          disabled={saving || quoting || !quote || (deliver && !schemas)}
          onClick={submit}
        >
          {saving
            ? "Placing order…"
            : quote && quote.deposit > 0
              ? `Place order and pay ${formatMoney(quote.deposit)} deposit`
              : "Place order"}
        </button>
        <Link to="/contractor/orders">Cancel</Link>
      </div>
    </div>
  );
}

export function NewContractorOrderPage() {
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireContractor title={TITLES.newOrder}>
          <NewOrderForm />
        </RequireContractor>
      </div>
    </div>
  );
}

function OrdersList() {
  const { data: orders, isLoading, error } = useContractorOrders();
  return (
    <div className={styles.stack}>
      <PageTitle
        actions={
          <Link className="btn" to="/contractor/orders/new">
            Start a job order
          </Link>
        }
      >
        {TITLES.orders}
      </PageTitle>
      <IconCard icon={<ClipboardIcon />} title="Orders">
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : error ? (
          <div className="notice notice--warn">{message(error)}</div>
        ) : !orders?.length ? (
          <p className="muted">
            No orders yet. <Link to="/contractor/orders/new">Start your first job order</Link>.
          </p>
        ) : (
          <JobOrdersTable orders={orders} />
        )}
      </IconCard>
    </div>
  );
}

/** Job orders: title, when, status and total, each linking to its page. */
export function JobOrdersTable({ orders }: { orders: ContractorOrder[] }) {
  return (
    <table className={styles.lines}>
      <thead>
        <tr>
          <th scope="col">Order</th>
          <th scope="col">When</th>
          <th scope="col">Status</th>
          <th scope="col" className={styles.num}>
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id}>
            <td>
              <Link to={`/contractor/orders/${o.id}`}>{o.title}</Link>
              <div className="muted">{o.id}</div>
            </td>
            <td>{whenText(o)}</td>
            <td>
              <span className={`${styles.chip} ${styles[o.status]}`}>{STATUS_LABELS[o.status]}</span>
            </td>
            <td className={styles.num}>{formatMoney(o.quote.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ContractorOrdersPage() {
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireContractor title={TITLES.orders}>
          <OrdersList />
        </RequireContractor>
      </div>
    </div>
  );
}

function DepositSection({ order, clientSecret: initial }: { order: ContractorOrder; clientSecret?: string }) {
  const [clientSecret, setClientSecret] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (clientSecret) return <DepositPayment clientSecret={clientSecret} />;
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      setClientSecret((await api.contractorDeposit(order.id)).clientSecret);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error && <div className="notice notice--warn">{error}</div>}
      <div>
        <button type="button" className="btn btn--lg" onClick={open} disabled={busy}>
          {busy ? "Opening…" : `Pay ${formatMoney(order.quote.deposit)} deposit`}
        </button>
      </div>
    </>
  );
}

function OrderDetail({ id }: { id: string }) {
  const [params] = useSearchParams();
  const { state } = useLocation() as { state: { clientSecret?: string } | null };
  const sessionId = params.get("session_id") ?? undefined;
  const { data: order, isLoading, error, refetch } = useContractorOrder(id, sessionId);
  // Back from paying, the deposit may still be settling: check again for about a minute, then
  // offer the payment again (a bank payment can take days, or the payment may not have finished).
  const [checks, setChecks] = useState(0);
  const settling = Boolean(sessionId) && order?.status === "awaiting_deposit" && checks < 20;
  useEffect(() => {
    if (!settling) return;
    const timer = setTimeout(() => {
      setChecks((n) => n + 1);
      void refetch();
    }, 3000);
    return () => clearTimeout(timer);
  }, [settling, checks, refetch]);

  if (isLoading) {
    return (
      <>
        <PageTitle>{TITLES.order}</PageTitle>
        <p className="muted">Loading…</p>
      </>
    );
  }
  if (error || !order) {
    return (
      <>
        <PageTitle>{TITLES.order}</PageTitle>
        <div className="notice notice--warn">{error ? message(error) : "Order not found."}</div>
      </>
    );
  }
  return (
    <div className={styles.stack}>
      <PageTitle
        subtitle={
          <span className={styles.meta}>
            <span className={`${styles.chip} ${styles[order.status]}`}>{STATUS_LABELS[order.status]}</span>
            <span>{whenText(order)}</span>
            <span className="muted">{order.id}</span>
          </span>
        }
      >
        {order.title}
      </PageTitle>
      <IconCard icon={<CartIcon />} title="Products">
        {order.description && <p className={styles.description}>{order.description}</p>}
        <LinesTable lines={order.quote.lines} />
        <QuoteSummary quote={order.quote} delivery={Boolean(order.delivery)} />
      </IconCard>
      <IconCard icon={<TruckIcon />} title={order.delivery ? "Delivery to the job site" : "Pickup"}>
        {order.delivery ? (
          <dl className={styles.facts}>
            <dt>When</dt>
            <dd>{deliveryWhen(order.neededBy, order.delivery.window)}</dd>
            <dt>Address</dt>
            <dd>
              {order.delivery.address.line1}
              {order.delivery.address.line2 && <>, {order.delivery.address.line2}</>}
              <br />
              {order.delivery.address.city}, {order.delivery.address.state} {order.delivery.address.postalCode}
            </dd>
            <dt>Job site supervisor</dt>
            <dd>
              {order.delivery.contactName},{" "}
              <a href={`tel:${order.delivery.contactPhone}`}>{formatPhone(order.delivery.contactPhone)}</a>
            </dd>
          </dl>
        ) : (
          <p>Pick it up at the store, ready by {formatDate(order.neededBy)}.</p>
        )}
      </IconCard>
      {order.status === "placed" ? (
        <IconCard icon={<WalletIcon />} title="Payment">
          <div className="notice">
            Order placed. Pay the {formatMoney(order.quote.total)}
            {order.quote.tax === null && " plus tax"}
            {order.delivery ? " when we deliver it" : " at the register when you pick it up"}.
          </div>
        </IconCard>
      ) : (
        <IconCard
          icon={<WalletIcon />}
          title={
            order.status !== "awaiting_deposit"
              ? "Deposit"
              : settling
                ? "Confirming your deposit"
                : "Pay the deposit to place the order"
          }
        >
          {order.status === "awaiting_deposit" ? (
            settling ? (
              <p className="muted">This usually takes a few seconds.</p>
            ) : (
              <>
                {sessionId && (
                  <p className="muted">
                    We haven&rsquo;t received this deposit yet. If you paid by bank transfer, it can take a few days to
                    arrive and you don&rsquo;t need to pay again.
                  </p>
                )}
                <DepositSection order={order} clientSecret={sessionId ? undefined : state?.clientSecret} />
              </>
            )
          ) : (
            <div className="notice">
              Deposit of {formatMoney(order.quote.deposit)} received
              {order.depositPaidAt && ` on ${formatDate(order.depositPaidAt)}`}. We&rsquo;ll bill the balance of{" "}
              {formatMoney(order.quote.balance)}
              {order.quote.tax === null && " plus tax"} when your order is ready.
            </div>
          )}
        </IconCard>
      )}
      <Link to="/contractor/orders">All orders</Link>
    </div>
  );
}

export function ContractorOrderPage() {
  const { id = "" } = useParams();
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireContractor title={TITLES.order}>
          <OrderDetail id={id} />
        </RequireContractor>
      </div>
    </div>
  );
}
